import { Loader2, X, ChevronRight, HelpCircle, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";

export const cx = (...a) => a.filter(Boolean).join(" ");

/* ---------- department classification (fallback for legacy strategists) ---------- */
// SDK specs define department directly (source: "sdk"). Legacy strategists have none,
// so we derive one from a static map (source: "generated"). No metadata is fabricated —
// generated classifications are clearly labelled and disappear as strategists migrate.
const DEPT_FALLBACK = {
  website: "Website", seo: "Marketing", marketing: "Marketing",
  brand: "Marketing", project: "Business",
};
const INDUSTRY_IDS = ["restaurant", "church", "nonprofit", "author", "coach", "contractor",
  "realestate", "fitness", "beauty", "healthcare", "legal", "ecommerce", "education", "photography"];

export function resolveDept(s) {
  if (s.department && s.department.trim()) return { name: s.department.trim(), source: "sdk" };
  if (DEPT_FALLBACK[s.id]) return { name: DEPT_FALLBACK[s.id], source: "generated" };
  if (INDUSTRY_IDS.includes(s.id)) return { name: "Industry Practices", source: "generated" };
  return { name: "Unassigned", source: "generated" };
}
export const DEPT_ORDER = ["Website", "Marketing", "Business", "Content", "Faith & Ministry", "Education", "Industry Practices", "Unassigned"];

export const confColor = (c) => (c >= 0.8 ? "text-emerald-400" : c >= 0.6 ? "text-gold" : "text-orange-400");
export const isSdk = (s) => s?.sdk_version && s.sdk_version !== "legacy";

/* ---------- primitives ---------- */
export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 text-gray-400 py-16 justify-center" data-testid="studio-loading">
    <Loader2 className="animate-spin text-gold" size={18} /> {label}
  </div>
);

export const Empty = ({ text, testid }) => (
  <div className="border border-dashed border-white/10 p-10 text-center text-gray-500 text-sm" data-testid={testid}>{text}</div>
);

export const Panel = ({ className = "", children, testid, ...p }) => (
  <div className={cx("bg-navy-800 border border-white/10 rounded-sm", className)} data-testid={testid} {...p}>{children}</div>
);

export const SectionLabel = ({ children, className = "" }) => (
  <div className={cx("text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400", className)}>{children}</div>
);

export function Metric({ label, value, sub, status, icon: Icon, className = "", testid }) {
  const dot = status === "healthy" ? "bg-emerald-400" : status === "warning" ? "bg-amber-400"
    : status === "error" ? "bg-red-400" : status === "dormant" ? "bg-slate-500" : null;
  return (
    <Panel className={cx("p-5", className)} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <SectionLabel>{label}</SectionLabel>
        {dot ? <span className={cx("w-2 h-2 rounded-full", dot)} /> : Icon ? <Icon size={15} className="text-gold" /> : null}
      </div>
      <div className="font-mono text-3xl font-light text-white tracking-tight leading-none">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </Panel>
  );
}

/* A Metric that drills into its underlying records. Pass `drill` (node or fn) to
 * enable the click-through drawer. Reuses Drawer — no bespoke modal per page. */
export function DrillDownMetric({ label, value, sub, status, icon: Icon, testid,
  drillTitle, drillSubtitle, drill }) {
  const [open, setOpen] = useState(false);
  const dot = status === "healthy" ? "bg-emerald-400" : status === "warning" ? "bg-amber-400"
    : status === "error" ? "bg-red-400" : status === "dormant" ? "bg-slate-500" : null;
  const clickable = !!drill;
  return (
    <>
      <Panel testid={testid}
        onClick={clickable ? () => setOpen(true) : undefined}
        className={cx("p-5 relative", clickable && "cursor-pointer hover:border-gold/40 transition-colors group")}>
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>{label}</SectionLabel>
          {dot ? <span className={cx("w-2 h-2 rounded-full", dot)} /> : Icon ? <Icon size={15} className="text-gold" /> : null}
        </div>
        <div className="font-mono text-3xl font-light text-white tracking-tight leading-none">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
        {clickable && (
          <span className="absolute bottom-2.5 right-3 text-[10px] text-slate-600 group-hover:text-gold inline-flex items-center gap-0.5 transition-colors" data-testid={`${testid}-drill-hint`}>
            Drill in <ChevronRight size={11} />
          </span>
        )}
      </Panel>
      {clickable && (
        <Drawer open={open} onClose={() => setOpen(false)} title={drillTitle || label} subtitle={drillSubtitle} testid={`${testid}-drill`}>
          {typeof drill === "function" ? drill() : drill}
        </Drawer>
      )}
    </>
  );
}

/* Reusable "Why?" AI-decision explainer. Every AI recommendation (model,
 * strategist, provider, routing, confidence) can attach one. reasons: string[];
 * factors: [{label, value}]. */
export function WhyButton({ title = "Why this decision?", subtitle, reasons = [], factors = [], note, testid = "why" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={testid}
        className="inline-flex items-center gap-1 text-[11px] text-gold border border-gold/40 hover:bg-gold/10 rounded-sm px-2 py-0.5 transition-colors">
        <HelpCircle size={11} /> Why?
      </button>
      <Drawer open={open} onClose={() => setOpen(false)} title={title} subtitle={subtitle} testid={`${testid}-drawer`}>
        {reasons.length > 0 && (
          <DBlock title="Reasoning">
            <ul className="space-y-2">
              {reasons.map((r, i) => (
                <li key={i} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /><span>{r}</span></li>
              ))}
            </ul>
          </DBlock>
        )}
        {factors.length > 0 && (
          <DBlock title="Factors weighed">
            <div className="space-y-1.5">
              {factors.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5">
                  <span className="text-slate-400">{f.label}</span>
                  <span className="text-white font-mono">{f.value}</span>
                </div>
              ))}
            </div>
          </DBlock>
        )}
        {note && <p className="text-xs text-slate-500 leading-relaxed">{note}</p>}
      </Drawer>
    </>
  );
}

/* Honest empty-state metric — no fabricated numbers. Use when a metric has no
 * real data source yet (data policy: never mock). */
export function AwaitingMetric({ label, reason = "Awaiting live data", icon: Icon, testid }) {
  return (
    <Panel className="p-5 border-dashed" testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <SectionLabel>{label}</SectionLabel>
        {Icon ? <Icon size={15} className="text-slate-600" /> : null}
      </div>
      <div className="text-slate-500 text-sm font-medium leading-tight">{reason}</div>
      <div className="text-[11px] text-slate-600 mt-2">Populates from real usage — no sample data shown.</div>
    </Panel>
  );
}

export function Badge({ children, tone = "slate", testid }) {
  const tones = {
    gold: "border-gold/50 text-gold bg-gold/10",
    slate: "border-slate-600 text-slate-400",
    green: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
    amber: "border-amber-500/40 text-amber-400 bg-amber-500/10",
    red: "border-red-500/40 text-red-400 bg-red-500/10",
  };
  return (
    <span data-testid={testid} className={cx("inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm", tones[tone])}>{children}</span>
  );
}

export function ConfBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 bg-white/10 rounded-sm overflow-hidden">
        <div className={cx("h-full", value >= 0.8 ? "bg-emerald-400" : value >= 0.6 ? "bg-gold" : "bg-orange-400")} style={{ width: `${pct}%` }} />
      </div>
      <span className={cx("font-mono text-xs w-9 text-right", confColor(value || 0))}>{pct}%</span>
    </div>
  );
}

/* Dense data table. columns: [{key,label,render?,className?}] */
export function DataTable({ columns, rows, onRowClick, rowTestid, empty = "No records.", testid }) {
  if (!rows || rows.length === 0) return <Empty text={empty} testid={testid ? `${testid}-empty` : undefined} />;
  return (
    <div className="overflow-x-auto border border-white/10 rounded-sm" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-navy-700 z-10">
          <tr className="text-left text-slate-400 text-[11px] uppercase tracking-wider">
            {columns.map((c) => <th key={c.key} className={cx("px-4 py-2.5 font-medium", c.className)}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} onClick={onRowClick ? () => onRowClick(row) : undefined}
              data-testid={rowTestid ? rowTestid(row) : undefined}
              className={cx("border-t border-white/5 text-slate-300", onRowClick && "cursor-pointer hover:bg-white/5 transition-colors")}>
              {columns.map((c) => <td key={c.key} className={cx("px-4 py-3", c.className)}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Right-side contextual drawer (backdrop blur). */
export function Drawer({ open, onClose, title, subtitle, children, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid={`${testid}-backdrop`}>
      <div className="bg-[#0A1128] border-l border-white/10 w-full max-w-lg h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="sticky top-0 bg-[#0A1128] border-b border-white/10 px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <h3 className="font-heading text-xl text-white leading-tight truncate">{title}</h3>
            {subtitle && <p className="text-slate-500 text-sm mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="text-slate-400 hover:text-white shrink-0 ml-4"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">{children}</div>
      </div>
    </div>
  );
}

export const DBlock = ({ title, children }) => (
  <div><SectionLabel className="text-gold mb-2">{title}</SectionLabel>{children}</div>
);
export const DList = ({ title, items }) => (items?.length > 0) ? (
  <DBlock title={title}><ul className="space-y-1.5">{items.map((x, i) => (
    <li key={i} className="text-slate-300 text-sm flex gap-2"><span className="text-gold shrink-0">·</span><span>{typeof x === "string" ? x : x.title || JSON.stringify(x)}</span></li>
  ))}</ul></DBlock>
) : null;
export const DKV = ({ label, value }) => value ? (
  <div className="flex gap-2 text-sm"><span className="text-slate-500 shrink-0">{label}:</span><span className="text-slate-300">{value}</span></div>
) : null;

/* Page frame — every module uses this so the header/question line is consistent. */
export function StudioPage({ title, question, actions, children, testid }) {
  return (
    <div data-testid={testid} className="max-w-[1400px]">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">{title}</h1>
          {question && <p className="text-slate-500 text-sm mt-1">{question}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
